/**
 * Platformer schema — content blocks.
 *
 * Split out of `platformer.schema.js` to keep both files comfortably
 * under the project's 1000-line cap. This module owns the bulky
 * authored content the schema imports for the inspector:
 *
 *   - FX_EMITTER_PRESETS — one-click look bags for the particle
 *     emitter (Fire / Smoke / Clouds / Leaves / Snow).
 *   - CAMERA_ZONE_INFO   — rich "what is a Camera Zone?" HTML, used
 *                          by the (i) bubble on the Zoom setting.
 *   - LIGHT_INFO         — rich "what is a Point Light?" HTML.
 *   - PLAYER_INTERACT_INFO — rich "what does Player Interaction do?"
 *                            HTML for the emitter inspector.
 *   - NOTE_INFO          — "what is a Music Note?" HTML for the note
 *                          element's (i) bubble.
 *
 * Pure data only — no Pixi, no runtime imports. The schema re-exports
 * each constant via its import binding.
 */

/**
 * One-click particle presets for the fx.emitter. Each `props` bag is a
 * COMPLETE look (it sets every tunable key, incl. `texture:''`, so
 * applying one always lands on a clean, predictable result — no leftover
 * settings from a previous preset). Angle convention: 0° = up, clockwise
 * (so 90 = right, 180 = down). The inspector renders these as a row of
 * buttons above the emitter's properties; clicking one overwrites the
 * look props (position + collision layer/mask are left untouched).
 */
export const FX_EMITTER_PRESETS = [
    {
        id: 'fire', label: 'Fire',
        props: {
            engine: 'normal', emit: 'continuous', texture: '',
            rate: 54, lifetime: 0.9, speedMin: 30, speedMax: 75,
            angle: 0, spread: 24, gravity: -130,
            radialAccel: 0, tangentialAccel: 0, damping: 0.4, spin: 0,
            turbulence: 60, turbScale: 0.05, turbSpeed: 2.5, sizeRandom: 0.35,
            startColor: '#ffd24a', endColor: '#ff3b1f',
            startSize: 16, endSize: 2, startAlpha: 0.95, endAlpha: 0,
            shape: 'soft', blend: 'add', spawnW: 18, spawnH: 6,
            maxParticles: 150, collide: 'off', bounce: 0.3, accumulate: 'off', playerInteract: 'off',
        },
    },
    {
        id: 'smoke', label: 'Smoke',
        props: {
            engine: 'normal', emit: 'continuous', texture: '',
            rate: 16, lifetime: 2.6, speedMin: 10, speedMax: 30,
            angle: 0, spread: 28, gravity: -26,
            radialAccel: 0, tangentialAccel: 0, damping: 0.6, spin: 18,
            turbulence: 50, turbScale: 0.02, turbSpeed: 0.8, sizeRandom: 0.45,
            startColor: '#6b7280', endColor: '#cbd5e1',
            startSize: 10, endSize: 36, startAlpha: 0.5, endAlpha: 0,
            shape: 'soft', blend: 'normal', spawnW: 16, spawnH: 8,
            maxParticles: 100, collide: 'off', bounce: 0.3, accumulate: 'off', playerInteract: 'off',
        },
    },
    {
        id: 'clouds', label: 'Clouds',
        props: {
            engine: 'normal', emit: 'continuous', texture: '',
            rate: 3, lifetime: 7, speedMin: 5, speedMax: 12,
            angle: 90, spread: 14, gravity: 0,
            radialAccel: 0, tangentialAccel: 0, damping: 0, spin: 0,
            turbulence: 0, turbScale: 0.02, turbSpeed: 1, sizeRandom: 0.3,
            startColor: '#ffffff', endColor: '#e5e7eb',
            startSize: 42, endSize: 50, startAlpha: 0.75, endAlpha: 0.35,
            shape: 'soft', blend: 'normal', spawnW: 64, spawnH: 28,
            maxParticles: 28, collide: 'off', bounce: 0.3, accumulate: 'off', playerInteract: 'off',
        },
    },
    {
        id: 'leaves', label: 'Leaves',
        props: {
            // Confetti engine = flat tumbling paper in a palette — a
            // perfect autumn-leaf look. Pairs great with Emit → "When
            // player is near" on a tree.
            engine: 'confetti', emit: 'continuous', texture: '',
            rate: 9, lifetime: 4, speedMin: 18, speedMax: 48,
            angle: 118, spread: 52, gravity: 48,
            radialAccel: 0, tangentialAccel: 34, damping: 0.3, spin: 0,
            turbulence: 70, turbScale: 0.025, turbSpeed: 1.2, sizeRandom: 0.3,
            palette: ['#4ade80', '#22c55e', '#16a34a', '#a3e635', '#ca8a04', '#b45309'],
            startSize: 11, endSize: 11, startAlpha: 1, endAlpha: 0.85,
            spawnW: 40, spawnH: 10,
            maxParticles: 70, collide: 'off', bounce: 0.3, accumulate: 'off',
            // The player parts the leaves as it runs through them (bow wave).
            playerInteract: 'push', interactStrength: 9, interactRadius: 70,
        },
    },
    {
        id: 'snow', label: 'Snow',
        props: {
            engine: 'normal', emit: 'continuous', texture: '',
            // Long lifetime so landed flakes linger as a real pile before
            // dying; a big pool keeps the falling snow + the heap on screen.
            rate: 26, lifetime: 14, speedMin: 12, speedMax: 30,
            angle: 180, spread: 18, gravity: 22,
            radialAccel: 0, tangentialAccel: 12, damping: 0.2, spin: 0,
            turbulence: 35, turbScale: 0.03, turbSpeed: 0.7, sizeRandom: 0.4,
            startColor: '#ffffff', endColor: '#eef4ff',
            startSize: 5, endSize: 5, startAlpha: 0.95, endAlpha: 0.85,
            shape: 'soft', blend: 'normal', spawnW: 80, spawnH: 8,
            maxParticles: 320, collide: 'off', bounce: 0.3,
            // Flakes stack into a real, organic pile on the ground that packs
            // denser as it grows (weight), and the player sweeps falling snow
            // into its wake as it runs through.
            accumulate: 'on', accumMax: 40, accumWeight: 0.4,
            playerInteract: 'wake', interactStrength: 7, interactRadius: 64,
        },
    },
];

/**
 * "What is a Camera Zone?" briefing for the (i) info bubble on the camera
 * zone's Zoom setting. Plain HTML styled by the `.se-help-body` popup.
 */
export const CAMERA_ZONE_INFO = `
  <p>A <b>Camera Zone</b> is an invisible rectangle that controls the camera
  while the player is inside it. Drag it out on the canvas (like an Area /
  Trigger), then set how it should frame the action.</p>

  <h4>Settings</h4>
  <ul>
    <li><b>Zoom</b> — how close the camera pulls in. <b>1</b> = normal,
      <b>&gt;1 zooms IN</b> (e.g. <code>1.8</code> for a tight, tense
      corridor or a boss close-up), <b>&lt;1 zooms OUT</b> (e.g.
      <code>0.7</code> to reveal a huge room). The view eases smoothly in
      when the player enters and back out when they leave.</li>
    <li><b>Focus</b> — <b>Follow the player</b> keeps the player centred at
      the new zoom (great for "zoom in on the action"). <b>Lock to zone
      centre</b> frames the rectangle itself and ignores the player — a
      scripted reveal / cutscene shot.</li>
    <li><b>Width / Height</b> — the region's size in cells (also drag-resizable
      on the canvas).</li>
  </ul>

  <h4>Examples</h4>
  <ul>
    <li><b>Tense chase:</b> a wide zone over a hallway, Zoom <code>1.6</code>,
      Focus = Follow the player.</li>
    <li><b>Boss reveal:</b> a zone around the arena entrance, Zoom
      <code>0.7</code>, Focus = Lock to zone centre.</li>
    <li><b>Secret room:</b> Zoom <code>1.3</code> to make a cosy alcove feel
      intimate.</li>
  </ul>

  <p>Outside every zone the camera follows the player at the stage's default
  zoom (set in the <b>Camera</b> panel when nothing is selected). Overlapping
  zones: the one on top wins. This only runs in <b>Test Play</b>.</p>
`;

/**
 * "What is a Point Light?" briefing for the (i) info bubble on the point
 * light's settings. Plain HTML styled by the `.se-help-body` popup.
 */
export const LIGHT_INFO = `
  <p>A <b>Point Light</b> is a glow placed in the world that brightens the
  scene around it. It only shows when the stage's <b>Lighting</b> is turned
  on (set it in the Lighting panel when nothing is selected) — that panel sets
  how dark the unlit world is, and lights carve brightness back into it.</p>

  <h4>Settings</h4>
  <ul>
    <li><b>Colour</b> — the tint of the glow (warm <code>#ffd9a0</code> for a
      torch, cool blue for moonlight, red for lava).</li>
    <li><b>Radius</b> — how far the glow reaches, in pixels. The reach ring in
      the editor shows it.</li>
    <li><b>Intensity</b> — how bright the light is at its core. Overlapping
      lights add together.</li>
    <li><b>Flicker</b> — <code>0</code> = steady; higher values make the
      light wobble like a flame or a failing bulb.</li>
  </ul>

  <h4>Examples</h4>
  <ul>
    <li><b>Torch:</b> warm colour, Radius <code>140</code>, Intensity
      <code>1.2</code>, Flicker <code>0.4</code>.</li>
    <li><b>Lava glow:</b> red, big Radius, low Flicker.</li>
    <li><b>Spotlight:</b> white, tight Radius, Intensity <code>1.6</code>.</li>
  </ul>

  <p>Lighting is composited as a single multiply pass at reduced resolution,
  so a handful of lights is cheap even on a phone. It only runs in
  <b>Test Play</b>.</p>
`;

/**
 * Rich "what is Player Interaction?" briefing shown by the (i) info bubble
 * next to the emitter's Player Interaction setting. Plain HTML, styled by
 * the `.se-help-body` popup. Covers every mode with a concrete example so a
 * designer knows exactly what each one does (and why it might "do nothing"
 * if the player isn't moving / isn't near).
 */
export const PLAYER_INTERACT_INFO = `
  <p><b>Player Interaction</b> lets the live player reshape the particles it
  runs through — like a car parting falling snow, a magnet collecting sparks,
  or wind swirling around the hero. Pick a <b>mode</b>, then dial
  <b>Strength</b> (how forceful) and <b>Range</b> (how far around the player
  it reaches; strongest at the player, fading to nothing at the edge).</p>

  <h4>Modes</h4>
  <ul>
    <li><b>Off</b> — particles ignore the player.</li>
    <li><b>Slipstream — drag along</b> — particles in range get pulled toward
      the player's <i>own velocity</i>, so they trail behind like a wake.
      <br><i>Example:</i> falling <b>snow / rain</b> swept sideways as a fast
      player runs through it. <em>Needs movement</em> — a standing player does
      nothing.</li>
    <li><b>Push aside — bow wave</b> — particles are shoved <i>outward</i> away
      from the player, harder the faster you move, so you part a field.
      <br><i>Example:</i> kicking up <b>leaves or dust</b> while sprinting
      through them. <em>Needs movement.</em></li>
    <li><b>Attract — pull in (magnet)</b> — particles are pulled <i>toward</i>
      the player and gather around it.
      <br><i>Example:</i> <b>coins, sparkles or fireflies</b> drawn to the hero.
      <em>Works even when the player stands still.</em></li>
    <li><b>Repel — push away (bubble)</b> — particles are pushed <i>away</i>,
      forming a clear bubble around the player.
      <br><i>Example:</i> a <b>shield / force-field</b> that keeps embers or
      spores off you. <em>Works while still.</em></li>
    <li><b>Swirl — orbit the player</b> — particles circle the player along a
      tangent.
      <br><i>Example:</i> <b>petals, ash or magic motes</b> orbiting the hero.
      <em>Works while still.</em></li>
  </ul>

  <h4>Settings</h4>
  <ul>
    <li><b>Strength</b> (0–20) — intensity of the effect. Slipstream & Push
      also scale with how fast the player moves.</li>
    <li><b>Range (px)</b> — radius of influence around the player.</li>
  </ul>

  <p><b>"It does nothing"?</b> Slipstream & Push only react while the player is
  <i>moving</i>; and any mode needs the player <i>inside the Range</i>. In the
  <b>editor</b> there's no real player, so the preview spawns a faint moving
  <b>ghost</b> over the emitter to demo the effect — for the real thing, hit
  <b>Test Play</b>.</p>
`;

/**
 * "What is a Music Note?" briefing for the (i) info bubble on the note
 * element. Walks the designer through binding a clip, the difference
 * between One-shot and Slice modes, and the auto vs. manual slice index.
 */
export const NOTE_INFO = `
  <p>A <b>Music Note</b> is a pass-through element that plays a sound when
  the player touches it — like the note tiles in Mario Wonder. It never
  blocks movement; it just sings.</p>

  <h4>How to use</h4>
  <ol>
    <li>Open the <b>Content Browser</b> → <b>Audio / Music</b> tab and
      <b>Import</b> a short MP3 / WAV / OGG (5–10 seconds is plenty).</li>
    <li>Place a Note in the world, then pick the imported clip in the
      note's <b>Audio</b> dropdown.</li>
    <li>Pick a <b>Mode</b> — One-shot plays the whole clip, Slice plays one
      consecutive chunk of it (perfect for chaining several notes into a
      synced tune).</li>
  </ol>

  <h4>Modes</h4>
  <ul>
    <li><b>One-shot — play the whole clip</b> — every contact plays the
      audio from the start. Good for SFX (a chime, a bird, a hop sound).</li>
    <li><b>Slice — sync several notes</b> — the clip is split into chunks
      of <b>Slice Duration</b> milliseconds (default 1000 ms = 1 s; lower
      for snappier melodies, e.g. 250 ms for a fast arpeggio) and each
      note in the stage that shares this audio plays its OWN slice. By
      default the <i>leftmost</i> note plays slice 0, the next plays
      slice 1, and so on \u2014 so a 5-second tune split across 5 notes
      plays in perfect order as the player runs across them. Override a
      single note's <b>Slice Index</b> to scramble or repeat slices.</li>
  </ul>

  <h4>Scheduled queue \u2014 the tune always plays at slice tempo</h4>
  <p>In a row of Music Notes sharing the same Audio clip, each
  triggered slice is <b>scheduled</b> onto a per-row queue on the
  audio clock: a new slice starts exactly when the previous one
  ends, so the tune always plays at its natural tempo \u2014 Slice
  Duration milliseconds per note \u2014 no matter how fast the player
  runs through. Touch every note in a single frame and the song
  still plays out in full over <code>notes \u00d7 Slice Duration</code>
  seconds, in order. Walking slower than slice tempo just leaves
  audible gaps between slices (the queue empties before the next
  trigger), which is the tune naturally tracking the player when
  they stop.</p>
  <p>Slice Duration directly controls the tempo: <b>1000 ms</b> is
  native pace, <b>500 ms</b> double-time, <b>250 ms</b> a fast
  arpeggio, <b>2000 ms</b> a slow chord. Tuning it down/up retunes
  the entire row in one move.</p>
  <p>The <b>last note</b> in the row (highest slice index) gets a
  controllable finale set via <b>End of tune</b>:</p>
  <ul>
    <li><b>Auto</b> \u2014 the last slice is extended by 5 seconds of
      extra audio from the same clip and faded smoothly to silence,
      so the tune ends on a held, decaying chord (Mario Wonder
      default).</li>
    <li><b>Fade</b> \u2014 use your own <b>Fade-out (ms)</b> length:
      500 for a short half-second decay, 2000 for two seconds,
      5000 for a long cinematic finale.</li>
    <li><b>Cut</b> \u2014 no extension, no fade. The last slice ends and
      the music stops dead. Use when you want a snappier loop or
      you\u2019re piping the row into an Audio Zone that takes over.</li>
  </ul>
  <p>Interior notes always play just their slice regardless of
  this setting \u2014 the End of tune only affects how the row\u2019s
  finale lands.</p>
  <p>A <i>solo</i> note (only one Music Note bound to a given clip)
  is treated as a quick SFX: it just plays its single slice
  fire-and-forget, no tail, no queue.</p>

  <h4>Settings</h4>
  <ul>
    <li><b>Audio</b> — the imported clip (any audio slot from the Content
      Browser).</li>
    <li><b>Slice Duration (ms)</b> \u2014 chunk length AND tempo, in
      milliseconds. Used both to compute this note's <i>offset</i>
      into the clip (Slice Index \u00d7 Slice Duration) and to schedule
      consecutive slices back-to-back at this tempo. 1000 ms = native
      pace (Mario Wonder feel), 500 ms = double-time, 250 ms = a fast
      arpeggio, 2000 ms = a slow chord.</li>
    <li><b>Slice Index</b> — leave at -1 for auto (left-to-right). Set a
      number to lock this note to that offset.</li>
    <li><b>Retrigger</b> — <b>Always</b> lets the player re-run the tune
      by walking back. <b>Once</b> consumes the note like a coin (plays
      its slice once and disappears, no group bookkeeping).</li>
    <li><b>End of tune</b> \u2014 how the LAST note in the row ends.
      <b>Auto</b> = 5 s fade-out tail (Mario Wonder default).
      <b>Fade</b> = use your own <b>Fade-out (ms)</b> length.
      <b>Cut</b> = no fade, no extension \u2014 the slice ends and the
      music stops dead.</li>
    <li><b>Fade-out (ms)</b> \u2014 length of the extra audio + linear
      fade-out applied to the LAST note when End of tune = Fade.
      0 (or End of tune = Cut) means no fade.</li>
    <li><b>Volume</b> — 0..1 per-note gain.</li>
  </ul>

  <p>Tip: clips are now persisted in <b>IndexedDB</b>, so they survive
  page reloads without filling the small localStorage quota. You can
  bind dozens of MB of music if you want.</p>

  <h4>Bulk edit</h4>
  <p>Marquee-select or Shift-click several notes and the inspector
  switches to <b>bulk-edit mode</b>: every change you make to Audio,
  Slice Duration, Mode, Tail, etc. applies to every selected note at
  once, in a single undo step. Slice Index stays per-note (so the
  auto left-to-right order keeps working).</p>

  <h4>Background music &amp; audio zones</h4>
  <p>For a level-wide soundtrack (not a per-touch tune), use an
  <b>Area / Trigger</b> with <b>Effect = Music</b>. A Play zone starts
  a clip as the current background music; a Stop zone fades it out;
  another Play zone elsewhere can crossfade into a different track.
  Drop them at level boundaries to swap soundtracks as the player
  progresses (e.g. menu \u2192 exploration \u2192 boss arena).</p>
`;
